import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { tokenRequest } from '../../authConfig';
import { reunionesPasadas, reunionesEntre } from '../../services/graphCalendar';
import { listarGrabaciones, emparejar } from '../../services/teamsGrabaciones';
import { listarMinutas, borrarMinuta, userIdDeCuenta } from '../../services/minutasV2Service';
import { FUENTE, useTema } from './tema';
import Cuenta from './Cuenta';
import { unir } from './emparejarMinutas';
import SubirTranscripcion from './SubirTranscripcion';
import { Leyendo } from './Ilustraciones';
import { useConfirmacion } from './Confirmacion';
import { datosDeFuente, sePuedeEliminar } from './fuenteDeMinuta';

/**
 * Inicio — la pantalla principal de la v2.
 *
 * Reúne en un sitio lo que antes estaba disperso: las reuniones pasadas del
 * calendario, las minutas ya generadas y los accesos para crear una nueva.
 *
 * Las reuniones salen del calendario de Outlook (Microsoft Graph). La app pedía
 * el permiso `Calendars.Read` desde el principio pero no lo usaba: no había una
 * sola llamada al calendario. Aquí empieza a usarse.
 */

const estilos = (C) => ({
  raiz: {
    display: 'flex', minHeight: '100vh', background: C.fondo, color: C.texto,
    fontFamily: FUENTE
  },
  principal: { flex: 1, padding: '40px 36px 80px', minWidth: 0 },
  saludo: { fontSize: 30, fontWeight: 600, textAlign: 'center', margin: '8px 0 4px' },
  pregunta: { fontSize: 20, color: C.suave, textAlign: 'center', margin: '0 0 48px', fontWeight: 400 },
  seccion: { fontSize: 17, fontWeight: 600, margin: '0 0 16px' },
  cabeceraSeccion: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },

  acciones: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 14, marginBottom: 48 },
  accion: (proximamente = false) => ({
    display: 'flex', alignItems: 'center', gap: 13, padding: '16px 18px',
    background: C.panel, border: `1px solid ${C.borde}`, borderRadius: 12, boxShadow: C.sombra,
    cursor: proximamente ? 'default' : 'pointer',
    textAlign: 'left', color: proximamente ? C.suave : C.texto,
    width: '100%', transition: 'background .15s',
    opacity: proximamente ? .62 : 1
  }),
  icono: (fondo, apagado = false) => ({
    width: 40, height: 40, borderRadius: 10, background: fondo, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19,
    filter: apagado ? 'saturate(.35)' : 'none'
  }),
  // «Próximamente» dicho una vez, en pequeño y sin color de alarma: es una
  // fecha que no ha llegado, no un error.
  pronto: {
    fontSize: 10.5, fontWeight: 600, letterSpacing: .3, textTransform: 'uppercase',
    padding: '2px 7px', borderRadius: 20, whiteSpace: 'nowrap',
    background: C.panelAlto, color: C.tenue, border: `1px solid ${C.borde}`
  },

  // El buscador va en la misma rejilla que las acciones, así que tiene que
  // medir lo mismo: mismo alto, mismo borde, mismo radio. Un control de otro
  // tamaño en una fila de iguales se lee como un error de maquetación, no como
  // una diferencia intencionada.
  tarjetaBuscar: {
    display: 'flex', alignItems: 'center', gap: 13, padding: '16px 18px',
    background: C.panel, border: `1px solid ${C.borde}`, borderRadius: 12,
    boxShadow: C.sombra, width: '100%', boxSizing: 'border-box'
  },
  campoBuscar: {
    flex: 1, minWidth: 0, border: 'none', background: 'transparent', outline: 'none',
    color: C.texto, fontSize: 14, fontFamily: FUENTE, padding: 0
  },

  tarjetas: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 16 },
  tarjeta: {
    background: C.panel, border: `1px solid ${C.borde}`, borderRadius: 12, boxShadow: C.sombra,
    overflow: 'hidden', cursor: 'pointer', display: 'flex', flexDirection: 'column'
  },
  miniatura: {
    height: 128, background: C.gradiente,
    display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative'
  },
  duracion: {
    position: 'absolute', right: 8, bottom: 8, background: C.velo, color: '#fff',
    padding: '2px 7px', borderRadius: 5, fontSize: 12, fontVariantNumeric: 'tabular-nums'
  },
  // ── Las otras dos vistas ────────────────────────────────────────────────
  //
  // El mosaico enseña cuatro reuniones por pantalla y media docena de píxeles
  // de degradado por cada una. Para quien tiene veinte al mes y viene a buscar
  // una concreta, eso es hacer scroll por una decoración. La lista y la tabla
  // son la misma información, más apretada: la tabla es la que viene puesta
  // porque es la que deja comparar fechas de un vistazo.
  filas: {
    border: `1px solid ${C.borde}`, borderRadius: 12, background: C.panel,
    boxShadow: C.sombra, overflow: 'hidden'
  },
  fila: {
    display: 'flex', alignItems: 'center', gap: 14, padding: '11px 15px',
    borderTop: `1px solid ${C.borde}`, cursor: 'pointer', textAlign: 'left',
    background: 'transparent', color: C.texto, width: '100%', fontFamily: FUENTE
  },
  filaIcono: {
    width: 34, height: 34, borderRadius: 9, background: C.gradiente, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16
  },
  filaTitulo: { fontSize: 14, fontWeight: 600, margin: 0, lineHeight: 1.35 },

  // ── La papelera ─────────────────────────────────────────────────────────
  //
  // Discreta en reposo —del color del texto apagado, sin fondo— y roja al
  // pasar por encima. Es lo contrario de lo que se hace con un boton principal
  // y es a proposito: esto no es lo que nadie viene a hacer, y una papelera
  // roja permanente en veinte filas convierte la lista en un campo de minas.
  //
  // Lo que no se negocia es el tamano. 30 px es el minimo con el que no se
  // pulsa por error la fila de al lado, y en un boton que borra sin vuelta eso
  // importa mas que lo que ocupa.
  borrar: {
    width: 30, height: 30, flexShrink: 0, borderRadius: 8,
    background: 'transparent', border: `1px solid ${C.borde}`, color: C.tenue,
    cursor: 'pointer', fontSize: 13, lineHeight: 1, fontFamily: FUENTE,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'color .14s ease, border-color .14s ease'
  },

  // De donde salio el acta. Mas tenue que el estado («con minuta»): es un dato
  // de procedencia, no algo que haya que mirar en cada fila.
  marcaFuente: {
    fontSize: 11.5, padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap',
    background: C.panelAlto, color: C.suave
  },
  filaMeta: { fontSize: 12, color: C.tenue, fontVariantNumeric: 'tabular-nums', marginTop: 2 },

  tabla: {
    width: '100%', borderCollapse: 'collapse', fontSize: 13.5,
    border: `1px solid ${C.borde}`, borderRadius: 12, overflow: 'hidden', background: C.panel
  },
  th: {
    textAlign: 'left', padding: '10px 14px', fontSize: 12, fontWeight: 600,
    color: C.tenue, borderBottom: `1px solid ${C.borde}`, whiteSpace: 'nowrap'
  },
  td: {
    padding: '10px 14px', borderTop: `1px solid ${C.borde}`,
    verticalAlign: 'middle', fontVariantNumeric: 'tabular-nums'
  },

  // El selector de vista: tres botones pegados, como un interruptor de tres
  // posiciones. Iconos y no palabras porque va en la misma línea que el título
  // y tres etiquetas de texto pesarían más que la sección que gobiernan.
  selectorVista: {
    display: 'inline-flex', border: `1px solid ${C.borde}`, borderRadius: 9,
    overflow: 'hidden', background: C.panel
  },
  botonVista: (activo) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 32, height: 28, padding: 0, border: 'none', cursor: 'pointer',
    background: activo ? C.panelAlto : 'transparent',
    color: activo ? C.texto : C.tenue
  }),

  cuerpoTarjeta: { padding: '12px 14px 14px' },
  tituloTarjeta: {
    fontSize: 14, fontWeight: 600, margin: '0 0 6px',
    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
  },
  fechaTarjeta: { fontSize: 12, color: C.tenue, fontVariantNumeric: 'tabular-nums' },

  lateral: (abierto) => ({
    width: abierto ? 320 : 46, flexShrink: 0, borderLeft: `1px solid ${C.borde}`,
    padding: abierto ? '32px 24px' : '32px 6px', background: C.fondoHondo,
    // Sin esto, al plegarse el contenido se apretuja en 46 px durante la
    // transición en vez de irse limpiamente.
    overflow: 'hidden', transition: 'width .16s ease, padding .16s ease'
  }),
  // Plegar el panel es un control, no un adorno: tiene que verse que se puede
  // pulsar. Era un carácter '›' suelto y del color más tenue del tema, a lo
  // ancho de una franja vacía —no parecía un botón ni se sabía dónde empezaba—.
  // Ahora es un círculo con borde, del tamaño de un dedo, pegado al canto por
  // el que se pliega cuando está abierto y centrado en la franja cuando no.
  plegar: (abierto) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 30, height: 30, padding: 0, marginBottom: 14, flexShrink: 0,
    marginLeft: 'auto', marginRight: abierto ? 0 : 'auto',
    borderRadius: '50%', background: C.panel, border: `1px solid ${C.borde}`,
    color: C.suave, cursor: 'pointer', fontFamily: FUENTE,
    transition: 'color .15s ease, border-color .15s ease'
  }),
  // El día, de canto, cuando el panel está plegado: una franja vacía no dice
  // que ahí haya nada, y nadie vuelve a abrir lo que parece un borde.
  cantoDia: {
    writingMode: 'vertical-rl', textOrientation: 'mixed', margin: '0 auto',
    fontSize: 12, color: C.tenue, fontVariantNumeric: 'tabular-nums',
    cursor: 'pointer', userSelect: 'none'
  },
  etiqueta: { fontSize: 13, color: C.suave, margin: '20px 0 7px', fontWeight: 500 },
  select: {
    width: '100%', padding: '10px 12px', borderRadius: 9, fontSize: 14,
    background: C.panelAlto, color: C.texto, border: `1px solid ${C.borde}`, boxSizing: 'border-box'
  },
  semana: { display: 'flex', justifyContent: 'space-between', margin: '22px 0 8px' },
  dia: (hoy, sel) => ({
    width: 34, height: 34, borderRadius: '50%', border: 'none', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
    background: sel ? C.acento : 'transparent',
    color: sel ? '#fff' : (hoy ? C.acento : C.texto),
    fontWeight: sel || hoy ? 600 : 400
  }),
  vacio: { textAlign: 'center', color: C.suave, fontSize: 15, marginTop: 56 },
  verMas: {
    padding: '9px 20px', borderRadius: 9, fontSize: 13.5, fontFamily: FUENTE, cursor: 'pointer',
    background: C.panel, color: C.texto, border: `1px solid ${C.borde}`
  }
});

const DIAS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

/** Duración en mm:ss o h:mm:ss, como en la tarjeta de una grabación. */
function duracion(inicio, fin) {
  if (!inicio || !fin) return null;
  const total = Math.max(0, Math.round((fin - inicio) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const dd = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${dd(h)}:${dd(m)}:${dd(s)}` : `${dd(m)}:${dd(s)}`;
}

function fechaHora(d) {
  if (!d) return '';
  const f = new Date(d);
  const dd = (n) => String(n).padStart(2, '0');
  return `${dd(f.getHours())}:${dd(f.getMinutes())} · ${dd(f.getDate())}/${dd(f.getMonth() + 1)}/${f.getFullYear()}`;
}

const Inicio = ({ onNuevaMinuta, onAbrirDetalle }) => {
  const { C, aviso } = useTema();
  const S = useMemo(() => estilos(C), [C]);

  /**
   * El panel lateral, plegado o no.
   *
   * Se recuerda entre visitas: plegarlo es una decisión sobre cómo se quiere
   * trabajar —dar el ancho a las minutas— y volver a encontrarlo abierto cada
   * vez sería no haberlo plegado. Vive en el navegador porque es de esta
   * persona en esta pantalla, no de su cuenta: en el portátil puede estorbar y
   * en el monitor grande no.
   *
   * El almacenamiento local puede estar bloqueado o lleno, y leerlo o escribirlo
   * lanza. Si falla, el panel sale abierto, que es lo de siempre.
   */
  const [lateralAbierto, setLateralAbierto] = useState(() => {
    try { return window.localStorage.getItem('minutas-v2-lateral') !== 'plegado'; }
    catch { return true; }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem('minutas-v2-lateral', lateralAbierto ? 'abierto' : 'plegado');
    } catch { /* que no se recuerde no es motivo para romper la pantalla */ }
  }, [lateralAbierto]);

  const { instance, accounts } = useMsal();
  const cuenta = accounts && accounts[0];
  const userId = userIdDeCuenta(cuenta);
  const nombre = (cuenta && cuenta.name) || 'de nuevo';

  const [pasadas, setPasadas] = useState([]);
  const [grabaciones, setGrabaciones] = useState([]);
  // ── La agenda lateral ────────────────────────────────────────────────
  //
  // Aquí vivían «las reuniones que quedan por delante hoy», y eso hacía que la
  // tira de días de abajo fuera decorativa: se podía pulsar el 18, el título
  // cambiaba, el círculo azul se movía, y la lista seguía siendo la de hoy. Con
  // la agenda de hoy vacía, cualquier día del que se tirara decía «No tienes
  // reuniones» aunque estuviera lleno.
  //
  // Y en el propio hoy tampoco salían todas: lo ya celebrado se descartaba, así
  // que a media tarde la mañana había desaparecido.
  //
  // Ahora se trae la semana entera y se filtra por el día elegido. Todas las de
  // ese día, las pasadas también — atenuadas, que es lo que distingue «ya fue»
  // de «te toca» sin esconder nada.
  const [agenda, setAgenda] = useState([]);
  const [minutas, setMinutas] = useState([]);
  const [diaSel, setDiaSel] = useState(new Date());
  const [busqueda, setBusqueda] = useState('');
  // Subir una transcripción abre su propio diálogo, no una pantalla aparte: lo
  // que se pide —archivo, título, fecha y formato— cabe de sobra, y mandar a
  // otra pantalla a quien solo quiere arrastrar un archivo es un viaje de ida y
  // vuelta por nada.
  const [subiendo, setSubiendo] = useState(false);
  // Las dos tarjetas —«Nueva minuta» y «Subir transcripción»— llevan al mismo
  // sitio porque hacen el mismo trabajo: de unas palabras dichas a un acta
  // guardada. Lo unico que cambia es de donde salen las palabras, y eso es por
  // donde se abre, no otra pantalla.
  const [via, setVia] = useState('archivo');
  // Cuántas tarjetas se pintan. Antes eran 10 fijas y sin decirlo: una reunión
  // de hace una semana desaparecía de la pantalla sin que nada indicara que
  // seguía ahí.
  const [mostrar, setMostrar] = useState(12);
  const [cargando, setCargando] = useState(true);
  // Las minutas llegan de la base de datos y las reuniones de Graph, por
  // separado y a distinta velocidad. Hasta que estén las dos no se sabe si una
  // minuta es una tarjeta propia o es la reunión que ya está en la lista, así
  // que se espera a ambas antes de pintar nada.
  const [minutasListas, setMinutasListas] = useState(false);

  // Cómo quiere ver sus reuniones. Se recuerda porque es una preferencia de
  // trabajo, no una opción de una visita: quien prefiere la tabla la prefiere
  // siempre, y volver a encontrar el mosaico cada mañana es empezar por
  // arreglar la pantalla.
  const [vista, setVista] = useState(() => {
    try {
      const guardada = window.localStorage.getItem('minutas-v2-vista');
      return ['mosaico', 'lista', 'tabla'].includes(guardada) ? guardada : 'tabla';
    } catch { return 'tabla'; }
  });

  useEffect(() => {
    try { window.localStorage.setItem('minutas-v2-vista', vista); }
    catch { /* que no se recuerde no es motivo para romper la pantalla */ }
  }, [vista]);
  const [errorCalendario, setErrorCalendario] = useState(null);

  // ── Borrar un acta desde la lista ─────────────────────────────────────────
  //
  // Solo las que se crearon subiendo algo: un texto pegado, un archivo de
  // transcripcion o un audio. Las de Teams no se borran desde aqui porque lo
  // que se ve en su tarjeta es la REUNION, y borrar el acta dejaria la tarjeta
  // en su sitio con la etiqueta cambiada: nadie entenderia que ha pasado. Esas
  // se borran desde su propia pantalla, donde esta claro que lo que se borra es
  // el acta y no la reunion.
  //
  // Las manuales son las que de verdad estorbaban: una prueba, un audio
  // transcrito mal, un .vtt subido dos veces. No tienen reunion detras, asi que
  // su fila es lo unico que existe de ellas y hasta hoy no habia forma de
  // quitarla.
  const [dialogo, confirmar] = useConfirmacion();
  const [borrando, setBorrando] = useState(null);
  const [errorBorrar, setErrorBorrar] = useState(null);

  const obtenerToken = useCallback(async () => {
    const r = await instance.acquireTokenSilent({ ...tokenRequest, account: cuenta });
    return r.accessToken;
  }, [instance, cuenta]);

  useEffect(() => {
    if (!cuenta) return;
    let vigente = true;

    (async () => {
      setCargando(true);
      try {
        const token = await obtenerToken();

        // Dos fuentes, no una. El calendario dice qué estaba previsto; las
        // grabaciones dicen qué ocurrió de verdad. Una reunión cuya cita se
        // borró, se declinó o nunca estuvo en el calendario existe igual si
        // Teams la grabó, y debe poder abrirse.
        // La semana que enseña la tira de días: de hoy a seis días vista.
        const desde = new Date();
        desde.setHours(0, 0, 0, 0);
        const hasta = new Date(desde);
        hasta.setDate(desde.getDate() + 6);
        hasta.setHours(23, 59, 59, 999);

        const [p, h, g] = await Promise.all([
          reunionesPasadas(token, 90),
          reunionesEntre(token, desde, hasta),
          listarGrabaciones(token, { dias: 90 }).catch(() => [])
        ]);
        if (!vigente) return;
        setPasadas(p);
        setAgenda(h);
        setGrabaciones(g);
        setErrorCalendario(null);
      } catch (e) {
        if (!vigente) return;
        // El calendario es una fuente más: si falla, la pantalla sigue sirviendo
        // para las minutas ya guardadas.
        setErrorCalendario(e.mensajeUsuario || e.message);
      } finally {
        if (vigente) setCargando(false);
      }
    })();

    return () => { vigente = false; };
  }, [cuenta, obtenerToken]);

  useEffect(() => {
    if (!userId) return;
    let vigente = true;

    setMinutasListas(false);
    listarMinutas(userId)
      .then(m => vigente && setMinutas(m))
      .catch(() => vigente && setMinutas([]))
      .finally(() => vigente && setMinutasListas(true));

    return () => { vigente = false; };
  }, [userId]);

  const semana = (() => {
    const base = new Date();
    const dias = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      dias.push(d);
    }
    return dias;
  })();

  const esHoy = (d) => d.toDateString() === new Date().toDateString();
  const esSel = (d) => d.toDateString() === diaSel.toDateString();

  // Lo del día que se está mirando. Se compara por la fecha del calendario y no
  // por horas: una reunión de las 8 y otra de las 20 son del mismo día.
  const delDia = agenda.filter(
    r => r.inicio && r.inicio.toDateString() === diaSel.toDateString()
  );

  /**
   * Una acción rápida.
   *
   * Con `proximamente` se enseña pero no se pulsa: la tarjeta sigue ahí —quien
   * la busca ve que existe y que está prevista— y se dice a las claras que
   * todavía no. Es preferible a esconderlas, que deja la pantalla sin explicar
   * por qué no se puede hacer lo que ayer se podía, y desde luego a dejarlas
   * pulsables llevando a medio sitio.
   */
  const accion = (icono, fondo, titulo, sub, onClick, proximamente = false) => (
    <button
      style={S.accion(proximamente)}
      onClick={proximamente ? undefined : onClick}
      disabled={proximamente}
      title={proximamente ? 'Todavía no está disponible' : undefined}
      key={titulo}
    >
      <span style={S.icono(fondo, proximamente)}>{icono}</span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600 }}>
          {titulo}
          {proximamente && <span style={S.pronto}>Próximamente</span>}
        </span>
        <span style={{ display: 'block', fontSize: 12.5, color: C.suave, marginTop: 1 }}>{sub}</span>
      </span>
    </button>
  );

  // Las reuniones del calendario, las grabaciones sin cita y las minutas
  // guardadas son tres fuentes de lo mismo. Se unen en una sola lista: una
  // minuta generada a partir de una reunión no es otra tarjeta, es esa misma
  // reunión con su minuta dentro.
  const reunionesTodas = [
    ...pasadas.map(r => ({ ...r, grabacion: emparejar(grabaciones, r) })),
    // Grabaciones que no corresponden a ninguna cita del calendario. Se muestran
    // igual: la reunión existió, y es justo la que si no se perdería.
    ...grabaciones
      .filter(g => !pasadas.some(r => {
        const emparejada = emparejar([g], r);
        return emparejada && emparejada.id === g.id;
      }))
      .map(g => ({
        id: `grabacion-${g.id}`,
        iCalUid: g.iCalUid,
        titulo: g.titulo,
        inicio: g.creada,
        fin: g.creada && g.duracion ? new Date(g.creada.getTime() + g.duracion * 1000) : null,
        asistentes: [],
        asistentesCorreo: [],
        organizador: null,
        soloGrabacion: true,
        grabacion: g
      }))
  ];

  const { entradas, sueltas } = unir(minutas, reunionesTodas);

  const todas = [
    ...entradas.map(({ reunion, minuta, minutasRepetidas }) => ({
      clave: `reu-${reunion.id}`,
      tipo: reunion.soloGrabacion ? 'grabacion' : 'reunion',
      titulo: reunion.titulo,
      fecha: reunion.inicio,
      duracion: duracion(reunion.inicio, reunion.fin),
      asistentes: reunion.asistentes ? reunion.asistentes.length : 0,
      organizador: reunion.organizador,
      grabacion: reunion.grabacion,
      minuteId: minuta ? minuta.minuteId : null,
      minutasRepetidas,
      reunion
    })),
    // Minutas cuya reunión ya no está en el rango consultado: se enseñan solas.
    ...sueltas.map(m => ({
      clave: `min-${m.minuteId}`,
      tipo: 'minuta',
      titulo: m.title,
      fecha: m.meetingDate || m.updatedAt || m.createdAt,
      minuteId: m.minuteId,
      // El acta entera y no solo su id: de aqui sale el origen —texto, archivo
      // o audio— que decide la etiqueta y si se ofrece borrarla.
      minuta: m
    }))
  ].sort((a, b) => new Date(b.fecha || 0) - new Date(a.fecha || 0));

  // Buscar sin tildes ni mayúsculas: nadie escribe "Plática" con acento al
  // buscar su propia reunión.
  const normalizar = (t) => String(t || '')
    .toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

  const filtradas = busqueda.trim()
    ? todas.filter(item =>
      normalizar(item.titulo).includes(normalizar(busqueda)) ||
      normalizar(item.organizador).includes(normalizar(busqueda)))
    : todas;

  const recientes = filtradas.slice(0, mostrar);

  // Nada se pinta a medias. Antes las minutas llegaban primero y sus tarjetas
  // aparecían marcadas «minuta»; al llegar el calendario resultaba que eran
  // reuniones ya conocidas, y el distintivo se movía o desaparecía delante de
  // quien estaba mirando.
  const listo = !cargando && minutasListas;

  // Abrir una fila y abrir una tarjeta es lo mismo; lo único que cambia entre
  // las tres vistas es cómo se dibuja.
  const abrir = (item) => {
    if (!onAbrirDetalle) return;
    // Minuta guardada y reunión del calendario abren la misma pantalla: cambia
    // de dónde saca los datos, no lo que se ve.
    onAbrirDetalle(
      item.tipo === 'minuta'
        ? { minuteId: item.minuteId }
        : {
          reunion: item.reunion,
          grabacion: item.grabacion || null,
          // Si ya tiene minuta se abre esa, en vez de la pantalla en blanco
          // que invitaba a generar otra.
          minuteId: item.minuteId || null
        }
    );
  };

  /**
   * Borrar un acta de las que se crearon subiendo algo.
   *
   * La fila desaparece de la lista local en vez de recargar del servidor: lo
   * que se acaba de borrar ya no esta, y volver a pedir las cien actas para
   * enterarse de lo que ya se sabe deja la pantalla parpadeando.
   */
  const eliminar = useCallback(async (item) => {
    if (!item || !item.minuta || borrando) return;

    const fuente = datosDeFuente(item.minuta);

    // El titulo entra en la pregunta a proposito. En una lista de veinte filas
    // parecidas, «¿seguro que quieres borrarla?» no dice CUAL, y el clic de
    // confirmar se da sobre un cuadro que no aporta nada.
    if (!await confirmar({
      titulo: 'Borrar el acta',
      mensaje: `Se va a borrar «${item.titulo || 'sin título'}».\n\n`
        + `${fuente.detalle} No hay reunión detrás de la que volver a generarla.\n\n`
        + 'Se borra también su historial de versiones. Esto no se deshace.',
      aceptar: 'Borrarla',
      peligro: true
    })) {
      return;
    }

    setBorrando(item.minuteId);
    setErrorBorrar(null);
    try {
      await borrarMinuta(item.minuteId, userId);
      setMinutas(ms => ms.filter(m => m.minuteId !== item.minuteId));
    } catch (e) {
      // El caso que pasa de verdad es un acta en ronda de firmas: el backend se
      // niega y su mensaje ya dice cuantas personas faltan y que hacer.
      setErrorBorrar(e.mensajeUsuario || e.message || 'No se pudo borrar el acta.');
    } finally {
      setBorrando(null);
    }
  }, [borrando, confirmar, userId]);

  /**
   * El boton de borrar de una fila.
   *
   * `stopPropagation` en los dos eventos y no solo en el clic: sin el del
   * `mousedown` el contenedor ya habia reaccionado antes de que llegara el
   * clic, y pulsar la papelera abria el acta ademas de preguntar si borrarla.
   */
  const botonBorrar = (item) => {
    if (!sePuedeEliminar(item.minuta)) return null;
    const estaBorrando = borrando === item.minuteId;
    return (
      <button
        style={S.borrar}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); eliminar(item); }}
        onMouseEnter={(e) => { e.currentTarget.style.color = C.error; e.currentTarget.style.borderColor = C.error; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = C.tenue; e.currentTarget.style.borderColor = C.borde; }}
        disabled={estaBorrando}
        title={estaBorrando ? 'Borrando…' : `Borrar esta acta (${datosDeFuente(item.minuta).etiqueta.toLowerCase()})`}
        aria-label={`Borrar el acta ${item.titulo || 'sin título'}`}
      >
        {estaBorrando ? '…' : '🗑'}
      </button>
    );
  };

  /**
   * De donde salio el acta, cuando no salio de una reunion.
   *
   * Es la respuesta a la pregunta que deja una fila suelta en el inicio: esta
   * no tiene ni cita ni grabacion, asi que «minuta» a secas no dice nada. Con
   * «Audio» o «Texto» se sabe de un vistazo cual era la prueba del martes y
   * cual el acta de verdad.
   */
  const marcaDeFuente = (item) => {
    if (item.tipo !== 'minuta' || !sePuedeEliminar(item.minuta)) return null;
    const fuente = datosDeFuente(item.minuta);
    return (
      <span style={S.marcaFuente} title={fuente.detalle}>
        <span aria-hidden="true">{fuente.icono}</span> {fuente.etiqueta}
      </span>
    );
  };

  /** En qué estado está: con acta, solo grabada, o una grabación sin cita. */
  const estadoDe = (item) => {
    if (item.tipo === 'minuta' || item.minuteId) return { texto: 'con minuta', fuerte: true };
    if (item.tipo === 'grabacion') return { texto: 'sin cita', fuerte: false };
    if (item.grabacion) return { texto: 'grabada', fuerte: false };
    return { texto: '—', fuerte: false };
  };

  const iconoDe = (item) => (item.tipo === 'minuta' ? '📝' : '🎥');

  const marca = (item) => {
    const { texto, fuerte } = estadoDe(item);
    if (texto === '—') return null;
    return (
      <span style={{
        fontSize: 11.5, padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap',
        background: fuerte ? C.acento : C.panelAlto,
        color: fuerte ? '#fff' : C.suave
      }}>
        {texto}
      </span>
    );
  };

  const vistaMosaico = () => (
    <div style={S.tarjetas}>
      {recientes.map(item => (
        <div key={item.clave} style={S.tarjeta} onClick={() => abrir(item)}>
          <div style={S.miniatura}>
            <span style={{ fontSize: 30, opacity: .55 }}>{iconoDe(item)}</span>
            {item.minuteId && item.tipo !== 'minuta' && (
              <span style={{ ...S.duracion, left: 8, right: 'auto', background: C.acento }}>
                con minuta
              </span>
            )}
            {item.tipo === 'reunion' && item.grabacion && !item.minuteId && (
              <span style={{ ...S.duracion, left: 8, right: 'auto' }}>grabada</span>
            )}
            {item.duracion && <span style={S.duracion}>{item.duracion}</span>}
            {item.tipo === 'minuta' && (
              <span style={{ ...S.duracion, left: 8, right: 'auto', background: C.acento }}>
                minuta
              </span>
            )}
            {item.tipo === 'grabacion' && (
              <span style={{ ...S.duracion, left: 8, right: 'auto' }}>sin cita</span>
            )}
            {/* Arriba a la derecha, lejos del centro de la tarjeta, que es por
                donde se pulsa para abrirla. */}
            {botonBorrar(item) && (
              <span style={{ position: 'absolute', top: 8, right: 8 }}>
                {botonBorrar(item)}
              </span>
            )}
          </div>
          <div style={S.cuerpoTarjeta}>
            <h3 style={S.tituloTarjeta}>{item.titulo}</h3>
            <div style={S.fechaTarjeta}>
              {fechaHora(item.fecha)}
              {item.asistentes > 0 && ` · ${item.asistentes} asistentes`}
              {item.minutasRepetidas > 1 && ` · ${item.minutasRepetidas} minutas guardadas`}
            </div>
            {marcaDeFuente(item) && (
              <div style={{ marginTop: 8 }}>{marcaDeFuente(item)}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  // ── Por que la fila ya no es un <button> ──────────────────────────────────
  //
  // Porque ahora lleva dentro el boton de borrar, y un boton dentro de otro
  // boton es HTML invalido: el navegador rompe el anidamiento por su cuenta y
  // el resultado depende de cual sea. Con `role="button"` y `tabIndex` la fila
  // sigue siendo un boton para quien usa teclado o lector de pantalla, que es
  // lo unico que daba el <button> y lo unico que habia que conservar.
  //
  // El Enter y el Espacio hay que atenderlos a mano por el mismo motivo: eso
  // es lo que un <button> hacia gratis.
  const vistaLista = () => (
    <div style={S.filas}>
      {recientes.map((item, i) => (
        <div
          key={item.clave}
          style={{ ...S.fila, borderTop: i === 0 ? 'none' : S.fila.borderTop }}
          onClick={() => abrir(item)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); abrir(item); }
          }}
        >
          <span style={S.filaIcono} aria-hidden="true">{iconoDe(item)}</span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ ...S.filaTitulo, display: 'block' }}>{item.titulo}</span>
            <span style={{ ...S.filaMeta, display: 'block' }}>
              {fechaHora(item.fecha)}
              {item.duracion && ` · ${item.duracion}`}
              {item.asistentes > 0 && ` · ${item.asistentes} asistentes`}
            </span>
          </span>
          {marcaDeFuente(item)}
          {marca(item)}
          {botonBorrar(item)}
        </div>
      ))}
    </div>
  );

  const vistaTabla = () => (
    <div style={{ overflowX: 'auto' }}>
      <table style={S.tabla}>
        <thead>
          <tr>
            <th style={S.th}>Reunión</th>
            <th style={S.th}>Fecha</th>
            <th style={S.th}>Duración</th>
            <th style={S.th}>Asistentes</th>
            <th style={S.th}>Origen</th>
            <th style={S.th}>Estado</th>
            {/* Sin titulo: una columna que dice «Acciones» sobre una sola
                papelera gasta ancho en explicar un icono que ya se entiende. */}
            <th style={S.th} aria-label="Borrar" />
          </tr>
        </thead>
        <tbody>
          {recientes.map(item => (
            <tr
              key={item.clave}
              onClick={() => abrir(item)}
              style={{ cursor: 'pointer' }}
            >
              <td style={{ ...S.td, fontWeight: 600, maxWidth: 460 }}>{item.titulo}</td>
              <td style={{ ...S.td, color: C.suave, whiteSpace: 'nowrap' }}>{fechaHora(item.fecha)}</td>
              <td style={{ ...S.td, color: C.suave }}>{item.duracion || '—'}</td>
              <td style={{ ...S.td, color: C.suave }}>{item.asistentes > 0 ? item.asistentes : '—'}</td>
              <td style={S.td}>
                {marcaDeFuente(item) || <span style={{ color: C.tenue }}>—</span>}
              </td>
              <td style={S.td}>{marca(item) || <span style={{ color: C.tenue }}>—</span>}</td>
              <td style={{ ...S.td, width: 1, whiteSpace: 'nowrap' }}>{botonBorrar(item)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Los tres iconos del selector, dibujados y no escritos: un cuadriculado, unos
  // renglones y una rejilla con cabecera.
  const DIBUJOS = {
    mosaico: <><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></>,
    lista: <><rect x="3" y="5" width="18" height="3" rx="1.5" /><rect x="3" y="10.5" width="18" height="3" rx="1.5" /><rect x="3" y="16" width="18" height="3" rx="1.5" /></>,
    tabla: <><rect x="3" y="4" width="18" height="16" rx="2" fillOpacity=".25" /><rect x="3" y="4" width="18" height="4.5" rx="2" /><rect x="11" y="9" width="1.4" height="11" /><rect x="3" y="13" width="18" height="1.4" /></>
  };

  const NOMBRES = { mosaico: 'Mosaico', lista: 'Lista', tabla: 'Tabla' };

  const selectorVista = (
    <div style={S.selectorVista} role="group" aria-label="Cómo ver las reuniones">
      {['mosaico', 'lista', 'tabla'].map(modo => (
        <button
          key={modo}
          style={S.botonVista(vista === modo)}
          onClick={() => setVista(modo)}
          title={NOMBRES[modo]}
          aria-label={NOMBRES[modo]}
          aria-pressed={vista === modo}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            {DIBUJOS[modo]}
          </svg>
        </button>
      ))}
    </div>
  );

  return (
    <div style={S.raiz}>
      <main style={S.principal}>
        <h1 style={S.saludo}>Saludos, {nombre}</h1>
        <p style={S.pregunta}>¿Cómo puedo ayudarte hoy?</p>

        <h2 style={S.seccion}>Acciones rápidas</h2>
        <div style={S.acciones}>
          {accion('📝', '#7c3aed', 'Nueva minuta', 'pegando la transcripción',
            () => { setVia('pegar'); setSubiendo(true); })}
          {accion('⬆️', '#059669', 'Subir transcripción', '.vtt, .srt, .txt, .json',
            () => { setVia('archivo'); setSubiendo(true); })}

          {/* Buscar es una acción más, y de las que más se usan. Estaba al lado
              del título de «Recientes», pequeña y a media pantalla de distancia:
              había que bajar a buscar el buscador. Aquí ocupa el hueco que dejó
              «Desde el calendario», cuya única función era precisamente hacer
              ese viaje por ti. */}
          <div style={S.tarjetaBuscar}>
            <span style={S.icono('#0891b2')} aria-hidden="true">🔍</span>
            <input
              style={S.campoBuscar}
              placeholder="Buscar una reunión…"
              value={busqueda}
              onChange={(e) => { setBusqueda(e.target.value); setMostrar(12); }}
              aria-label="Buscar una reunión"
            />
          </div>
        </div>

        <div id="recientes-v2" style={S.cabeceraSeccion}>
          <h2 style={{ ...S.seccion, margin: 0 }}>Recientes</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {errorCalendario && <span style={{ fontSize: 12.5, color: C.tenue }}>solo minutas guardadas</span>}
            {/* Cuando se está filtrando hay que decirlo aquí: el buscador quedó
                arriba, y una lista recortada sin explicación parece una lista
                que ha perdido cosas. */}
            {busqueda.trim() && (
              <span style={{ fontSize: 12.5, color: C.tenue }}>
                {filtradas.length === 1 ? '1 coincidencia' : `${filtradas.length} coincidencias`}
                {' · '}
                <button
                  onClick={() => setBusqueda('')}
                  style={{
                    background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
                    color: C.acentoClaro, fontSize: 12.5, fontFamily: FUENTE
                  }}
                >
                  quitar el filtro
                </button>
              </span>
            )}
            {selectorVista}
          </div>
        </div>

        {errorCalendario && <div style={aviso('error')}>{errorCalendario}</div>}

        {/* Un borrado que no se pudo hacer. El caso real es un acta en ronda
            de firmas: el backend se niega y su mensaje dice cuantas personas
            faltan. Se queda hasta que se cierra; quitarlo solo dejaria a
            alguien creyendo que el acta se borro. */}
        {errorBorrar && (
          <div
            style={{ ...aviso('error'), display: 'flex', alignItems: 'flex-start', gap: 10 }}
            role="alert"
          >
            <span style={{ flex: 1 }}>{errorBorrar}</span>
            <button
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'inherit', fontSize: 17, lineHeight: 1, padding: 0, fontFamily: FUENTE
              }}
              onClick={() => setErrorBorrar(null)}
              aria-label="Cerrar el aviso"
            >
              ×
            </button>
          </div>
        )}

        {!listo && <Leyendo texto="Leyendo reuniones…" color={C.suave} acento={C.acento} />}

        {listo && recientes.length === 0 && busqueda.trim() && (
          <div style={{ color: C.suave, fontSize: 14 }}>
            Ninguna de tus {todas.length} reuniones de los últimos 90 días coincide con “{busqueda}”.
          </div>
        )}

        {listo && todas.length === 0 && (
          <div style={{ color: C.suave, fontSize: 14 }}>
            Todavía no hay reuniones ni minutas. Empieza subiendo una transcripción.
          </div>
        )}

        {listo && recientes.length > 0 && (
          vista === 'mosaico' ? vistaMosaico()
            : vista === 'lista' ? vistaLista()
              : vistaTabla()
        )}

        {listo && filtradas.length > recientes.length && (
          <div style={{ textAlign: 'center', marginTop: 22 }}>
            <button style={S.verMas} onClick={() => setMostrar(m => m + 24)}>
              Ver más ({filtradas.length - recientes.length} sin mostrar)
            </button>
          </div>
        )}

      </main>

      <aside style={S.lateral(lateralAbierto)}>
        <button
          style={S.plegar(lateralAbierto)}
          onClick={() => setLateralAbierto(v => !v)}
          onMouseEnter={(e) => { e.currentTarget.style.color = C.texto; e.currentTarget.style.borderColor = C.suave; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = C.suave; e.currentTarget.style.borderColor = C.borde; }}
          title={lateralAbierto ? 'Plegar el panel' : 'Abrir el panel'}
          aria-label={lateralAbierto ? 'Plegar el panel' : 'Abrir el panel'}
          aria-expanded={lateralAbierto}
        >
          <svg
            width="15" height="15" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
            aria-hidden="true"
            style={{
              // Un solo dibujo para los dos estados: la flecha gira, y así el
              // sentido del gesto se entiende sin leer el título.
              transform: lateralAbierto ? 'none' : 'rotate(180deg)',
              transition: 'transform .16s ease'
            }}
          >
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>

        {!lateralAbierto && (
          <div
            style={S.cantoDia}
            onClick={() => setLateralAbierto(true)}
            title="Abrir el panel"
          >
            {diaSel.toLocaleDateString('es', { day: 'numeric', month: 'short' })}
          </div>
        )}

        {lateralAbierto && (
        <>
        <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>
          {diaSel.toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' })}
        </div>

        <div style={S.semana}>
          {semana.map((d, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: C.tenue, marginBottom: 5 }}>{DIAS[d.getDay()]}</div>
              <button style={S.dia(esHoy(d), esSel(d))} onClick={() => setDiaSel(d)}>
                {d.getDate()}
              </button>
            </div>
          ))}
        </div>

        {delDia.length === 0 ? (
          <div style={S.vacio}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>No tienes reuniones</div>
            <div style={{ fontSize: 14, color: C.tenue }}>
              {/* Decir «no queda nada por hoy» mirando el jueves que viene era
                  contestar a una pregunta que nadie había hecho. */}
              {esSel(new Date())
                ? 'Ya estás al día. No queda nada por hoy.'
                : 'Ese día está libre.'}
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 22 }}>
            {delDia.map(r => {
              // Ya celebrada. Se sigue viendo —es lo que se fue a buscar— pero
              // apagada: de un vistazo se distingue lo que queda de lo que pasó.
              const pasada = r.fin && r.fin < new Date();
              return (
                <div key={r.id} style={{
                  padding: '11px 13px', background: C.panel, borderRadius: 9,
                  marginBottom: 9, border: `1px solid ${C.borde}`,
                  opacity: pasada ? .55 : 1
                }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>{r.titulo}</div>
                  <div style={{ fontSize: 12, color: C.tenue, marginTop: 3 }}>
                    {r.inicio && r.inicio.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}
                    {r.esEnLinea && ' · en línea'}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ marginTop: 28, paddingTop: 20, borderTop: `1px solid ${C.borde}` }}>
          <button
            style={{ ...S.verMas, width: '100%', marginBottom: 16 }}
            onClick={() => { window.location.hash = '#v2/config'; }}
          >
            ⚙ Configuración
          </button>
          <Cuenta />
        </div>
        </>
        )}
      </aside>

      {dialogo}

      {subiendo && (
        <SubirTranscripcion
          userId={userId}
          entradaInicial={via}
          onCerrar={() => setSubiendo(false)}
          onCreada={({ minuteId }) => {
            setSubiendo(false);
            // Se abre el acta recién hecha. Dejar a la persona en el inicio con
            // un «listo» la obligaria a buscar en la lista lo que acaba de
            // crear, y es lo unico que iba a hacer a continuacion.
            if (onAbrirDetalle) onAbrirDetalle({ minuteId });
          }}
        />
      )}
    </div>
  );
};

export default Inicio;
